/**
 * "Como chegar" — quem escolhe o app é o TELEFONE, não o Viajandos.
 *
 * Uma PWA não sabe quais apps estão instalados — não há API para isso em
 * iOS nem em Android. O app já perguntou "qual app?" num sheet próprio, com
 * "sempre abrir com este" guardado em `localStorage`; o teste físico mostrou
 * que isso duplica o que o telefone já faz. Hoje:
 *
 *  - **Android**: `geo:` — o sistema mostra o seletor com os apps de mapa
 *    instalados, e "sempre" é configuração do aparelho;
 *  - **iOS**: não existe seletor de sistema; o link universal do Apple Maps
 *    é o único que abre em todo iPhone;
 *  - **computador**: Google Maps na web.
 *
 * As funções por app continuam existindo para quem precisar de um link
 * específico (o Ponto de encontro compartilha por texto, por exemplo).
 */
import { hasCoordinates, type Activity } from './itinerary.js';

export const NAVIGATION_APPS = ['google', 'apple', 'waze'] as const;
export type NavigationApp = (typeof NAVIGATION_APPS)[number];

export const NAVIGATION_APP_LABELS: Record<NavigationApp, string> = {
  google: 'Google Maps',
  apple: 'Apple Maps',
  waze: 'Waze',
};

export type NavigationPlatform = 'ios' | 'android' | 'other';

/** Apps que fazem sentido oferecer em cada plataforma, na ordem do sheet. */
export const NAVIGATION_APPS_BY_PLATFORM: Record<NavigationPlatform, readonly NavigationApp[]> = {
  ios: ['apple', 'google', 'waze'],
  android: ['google', 'waze'],
  other: ['google'],
};

export function isNavigationApp(v: unknown): v is NavigationApp {
  return typeof v === 'string' && (NAVIGATION_APPS as readonly string[]).includes(v);
}

/**
 * iPadOS se apresenta como Mac no user agent; a diferença é o toque.
 * `maxTouchPoints` chega separado por isso.
 */
export function platformFromUserAgent(userAgent: string, maxTouchPoints = 0): NavigationPlatform {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return 'ios';
  if (/Macintosh/i.test(userAgent) && maxTouchPoints > 1) return 'ios';
  if (/Android/i.test(userAgent)) return 'android';
  return 'other';
}

export type NavigationTarget = Pick<Activity, 'locationName' | 'placeId' | 'latitude' | 'longitude'>;

export interface NavigationLink {
  app: NavigationApp;
  label: string;
  url: string;
}

/**
 * URL de rota em um app. Com coordenadas, a rota é exata; só com o nome, é
 * uma busca. Sem nada, não há para onde ir.
 */
export function navigationUrl(app: NavigationApp, a: NavigationTarget): string | null {
  const nome = a.locationName?.trim() || '';
  if (hasCoordinates(a)) {
    const ll = `${a.latitude},${a.longitude}`;
    switch (app) {
      case 'google': {
        const placeId = a.placeId ? `&destination_place_id=${encodeURIComponent(a.placeId)}` : '';
        return `https://www.google.com/maps/dir/?api=1&destination=${ll}${placeId}`;
      }
      case 'apple': {
        const q = nome ? `&q=${encodeURIComponent(nome)}` : '';
        return `https://maps.apple.com/?daddr=${ll}${q}`;
      }
      case 'waze':
        return `https://waze.com/ul?ll=${ll}&navigate=yes`;
    }
  }
  if (!nome) return null;
  const q = encodeURIComponent(nome);
  switch (app) {
    case 'google':
      return `https://www.google.com/maps/search/?api=1&query=${q}`;
    case 'apple':
      return `https://maps.apple.com/?q=${q}`;
    case 'waze':
      return `https://waze.com/ul?q=${q}&navigate=yes`;
  }
}

/** As opções do sheet para esta plataforma; vazio quando não há destino. */
export function navigationLinks(a: NavigationTarget, platform: NavigationPlatform): NavigationLink[] {
  const links: NavigationLink[] = [];
  for (const app of NAVIGATION_APPS_BY_PLATFORM[platform]) {
    const url = navigationUrl(app, a);
    if (url) links.push({ app, label: NAVIGATION_APP_LABELS[app], url });
  }
  return links;
}

/**
 * O link que o botão "Como chegar" usa nesta plataforma. Nulo sem destino.
 *
 * O `geo:` do Android leva coordenadas quando há, e só a busca quando não;
 * o rótulo entre parênteses é o que o app mostra no pin.
 */
export function deviceNavigationUrl(a: NavigationTarget, platform: NavigationPlatform): string | null {
  if (platform === 'android') {
    const nome = a.locationName?.trim() || '';
    if (hasCoordinates(a)) {
      const ll = `${a.latitude},${a.longitude}`;
      const rotulo = nome ? `(${encodeURIComponent(nome)})` : '';
      return `geo:${ll}?q=${ll}${rotulo}`;
    }
    return nome ? `geo:0,0?q=${encodeURIComponent(nome)}` : null;
  }
  return navigationUrl(platform === 'ios' ? 'apple' : 'google', a);
}
